/**
 * Open: one scrolling page, four numbered steps, a sticky footer.
 *
 * ONE SCREEN, NO WIZARD. Every step is visible at once and none of them hides
 * behind a sheet — the only things that open over the page are the two lists
 * too long to sit inline (the rounds, and the players in a round). The four
 * steps replaced a chain of sheets and a full-page form, where the thing you
 * were building was never all on screen at the same time.
 *
 * THE ROUND IS THE PIVOT. It decides who is eligible (the round's own field),
 * what kind of bet makes sense (step 2 defaults from its format), and what the
 * scoring can be — everything below step 1 hangs off it.
 *
 * NOBODY IS PRE-SELECTED. A head to head opens with two empty slots. Seeding
 * side one with the signed-in member is what made this screen read as "your
 * bet", when most of them are put on by whoever is running the day.
 *
 * LOGGING IS OPTIMISTIC — the entry joins the list below and the toast offers
 * Undo; if the server disagrees the page rolls back from the pre-mutation
 * snapshot with the error on screen. Same pattern as `PlayHub`'s join.
 *
 * A LOGGED BET MUST BE SEEN TO LAND. After a confirm the builder gives way
 * to a card saying what was logged, with "Add another bet" to bring it back.
 * A sweep is why: it has no slots to clear, so the screen used to look the
 * same after Confirm as before it, with the button still live — and the
 * natural reading was that it had not gone through, so it was confirmed
 * again. A bet on the very same terms as one already open asks first
 * (`findDuplicate`). Every reload after a write asks for THE ROUND ON SCREEN. It used to reload with no round,
 * which the server answers with its default — so a bet logged on any other
 * round sent the page back to that one, and the entry just confirmed was on
 * neither the list nor the screen.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApi } from '../useApi';
import { Avatar, Card, money } from './primitives';
import {
  BET_TYPES, DEFAULT_SPLITS, allowedBetTypes, betSummary, defaultBetType,
  defaultScoring, entryAmount, entryTitle, findDuplicate, loggedView,
  roundDescription, roundTitle,
  MAX_PLACES, ordinal, shortName, splitMoney, splitsValid,
} from './format';
import { PlayerPickerModal, RoundSheet } from './Pickers';
import ConfirmationModal from '../components/ConfirmationModal';

const TOAST_MS = 6000;
const PRESETS = [5, 10, 20, 50];

const SPLIT_OPTIONS = [
  { places: 1, label: 'Winner takes all' },
  { places: 2, label: 'Top 2' },
  { places: 3, label: 'Top 3' },
];

const Step = ({ number, title, hint, children }) => (
  <section className="pr-step">
    <header className="pr-step-head">
      {number ? <span className="pr-step-badge">{number}</span> : null}
      <span className="pr-step-title">{title}</span>
      {hint ? <span className="pr-step-hint">{hint}</span> : null}
    </header>
    {children}
  </section>
);

/**
 * An empty or filled player slot. Tapping either opens the picker.
 *
 * A FILLED SLOT SAYS "change", because a filled slot that looks final is one
 * nobody tries to tap. It also carries the handicap, which is the figure you
 * actually weigh a match on.
 */
const Slot = ({ player, placeholder, onClick, size = 44, compact = false }) => (
  <button type="button"
          className={`pr-slot${player ? ' is-filled' : ''}${
            compact ? ' pr-slot--compact' : ''}`}
          onClick={onClick}>
    {player ? (
      <>
        <Avatar name={player.name} size={size} />
        <span className="pr-slot-name">{shortName(player.name)}</span>
        <span className="pr-slot-meta">
          {player.handicap != null ? `Hcp ${player.handicap} · ` : ''}change
        </span>
      </>
    ) : (
      <>
        <span className="pr-slot-plus" aria-hidden="true">＋</span>
        <span className="pr-slot-placeholder">{placeholder}</span>
        <span className="pr-slot-meta">Tap to pick</span>
      </>
    )}
  </button>
);

const PredictionsHub = () => {
  const { authenticatedFetch } = useApi();

  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [field, setField] = useState(null);

  const [type, setType] = useState(null);
  const [sides, setSides] = useState([[], []]);
  const [stake, setStake] = useState(10);
  const [scoring, setScoring] = useState('nett');
  const [places, setPlaces] = useState(1);
  // Null means "use the default shares for this many places". A society that
  // sets its own carries them here until it changes the place count.
  const [shares, setShares] = useState(null);

  const [roundSheet, setRoundSheet] = useState(false);
  const [picking, setPicking] = useState(null);      // { side, slot }
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [toast, setToast] = useState(null);
  const [removing, setRemoving] = useState(null);   // the entry being deleted
  // The entry just confirmed: marked in the list until the next write, which
  // outlives the toast — the toast is gone in six seconds, and that is about
  // how long it takes to scroll down and look.
  const [justLogged, setJustLogged] = useState(null);
  // What the last confirm logged, while the builder is put away. Null is the
  // builder showing.
  const [logged, setLogged] = useState(null);
  const [duplicate, setDuplicate] = useState(null);  // an identical open bet
  const toastTimer = useRef(null);
  const scrollToLogged = useRef(false);
  const loggedCard = useRef(null);

  const load = useCallback(async ({ quiet = false, pick } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const query = !pick ? ''
        : pick.scope === 'overall'
          ? `?tournament=${pick.tournament_id}&scope=overall`
          : `?tournament=${pick.tournament_id}&round=${pick.round_number}`;
      const res = await authenticatedFetch(`/predictions/hub${query}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setHub(await res.json());
      setLoadError(null);
    } catch (err) {
      // A FAILED REQUEST IS NOT AN ANSWER. Blanking the page would paint
      // "nothing on" over a round that exists — see `home_blank_data_bug`.
      setLoadError('Could not load your predictions. Pull to retry.');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const round = hub?.round || null;
  // This round is COMPLETE: visible, but no longer bettable. An entry can be
  // struck right through the round now and only closes when every card is in
  // (see `_bettable_rounds`). The SERVER decides — it refuses the write
  // either way, and a rule with a copy in JS is a rule that drifts.
  const locked = !!round?.locked;
  const rounds = hub?.rounds || [];
  const draw = hub?.draw || [];
  const myPlayerId = hub?.me?.player_id ?? null;
  const tournamentId = round?.tournament_id;
  const roundNumber = round?.round_number;

  /** The round's own field — the only people who may be named. */
  const players = useMemo(
    () => field || hub?.players || [], [field, hub]);

  const byId = useMemo(() => {
    const map = {};
    players.forEach((p) => { map[p.player_id] = p; });
    return map;
  }, [players]);

  const nameOf = useCallback((id) => byId[id]?.name || 'Player', [byId]);

  const allowed = useMemo(() => allowedBetTypes(round), [round]);
  const activeType = type && allowed.includes(type) ? type : allowed[0];

  // The hub carries a shortlist; a pairs card or a sweep needs the roster.
  useEffect(() => {
    let cancelled = false;
    setField(null);
    if (!tournamentId) return undefined;
    (async () => {
      try {
        const res = await authenticatedFetch(
          `/tournaments/${tournamentId}/players`);
        if (!res.ok || cancelled) return;
        const rows = await res.json();
        if (!cancelled) {
          setField(rows.map((p) => ({
            player_id: p.id, name: p.name, handicap: p.handicap,
          })));
        }
      } catch (err) { /* the shortlist still works */ }
    })();
    return () => { cancelled = true; };
  }, [tournamentId, roundNumber, authenticatedFetch]);

  /**
   * Start the build over whenever the round changes: its field, its format and
   * its draw are all different, so nothing carried over would still be true.
   *
   * A PAIRS MATCH PLAY ROUND ARRIVES ALREADY DRAWN, so the teams come from the
   * day's own pairings and the only thing left is the stake.
   */
  const drawSignature = draw.length;
  useEffect(() => {
    if (!round) return;
    const next = defaultBetType(round);
    setType(next);
    setScoring(defaultScoring(round, next));
    setPlaces(1);
    setShares(null);
    setFormError(null);
    setLogged(null);
    const firstMatch = draw[0];
    setSides(next === 'pairs' && firstMatch
      ? firstMatch.sides.map((side) => side.map((p) => p.player_id))
      : [[], []]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId, roundNumber, round?.scope, drawSignature]);

  const perSide = activeType === 'pairs' ? 2 : 1;

  const prefilledFromDraw = activeType === 'pairs' && round?.pairs_matchplay
    && sides[0].length === 2 && sides[1].length === 2;

  // A sweep enters everybody: there is nothing to choose, so nothing is asked.
  const entrants = activeType === 'sweep'
    ? players.map((p) => p.player_id) : [];

  const pot = stake * entrants.length;

  // A custom split that does not add to 100 is not a set of terms, so it
  // cannot be confirmed. The server refuses it too — this is the screen
  // agreeing with the server, not the guard.
  const sharesOk = !shares || splitsValid(shares, places);

  const valid = activeType === 'sweep'
    ? entrants.length >= 2 && stake > 0 && sharesOk
    : sides[0].length === perSide && sides[1].length === perSide && stake > 0;

  const summary = betSummary({
    type: activeType, sides, entrants, format: scoring, nameOf,
  });

  const total = activeType === 'sweep' ? pot : stake;

  const assign = (sideIndex, slotIndex, playerId) => {
    setSides((current) => {
      // Taking somebody already placed MOVES them rather than refusing: it is
      // a correction, not a mistake.
      const next = current.map((side) => side.filter((x) => x !== playerId));
      const side = [...next[sideIndex]];
      side[slotIndex] = playerId;
      next[sideIndex] = side.filter((x) => x != null);
      return next;
    });
    setPicking(null);
    setFormError(null);
  };

  const clearSlot = (sideIndex, slotIndex) => {
    setSides((current) => {
      const next = current.map((side) => [...side]);
      next[sideIndex] = next[sideIndex].filter((_, i) => i !== slotIndex);
      return next;
    });
    setPicking(null);
  };

  const showToast = (next) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(next);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  };

  const confirm = async () => {
    const snapshot = hub;
    setSubmitting(true);
    setFormError(null);
    try {
      const payload = {
        kind: activeType,
        format: scoring,
        stake,
        tournament_id: round.tournament_id,
        round_number: round.round_number ?? null,
        sides: activeType === 'sweep' ? [entrants] : sides,
      };
      if (activeType === 'pairs') payload.config = { variant: 'fourball' };
      if (activeType === 'sweep') {
        payload.config = { payout_mode: 'pot', places_paid: places };
        // Only send shares that differ from the default: a society that has
        // not touched them should not have today's defaults frozen onto a
        // bet that would otherwise follow them.
        if (shares) payload.config.splits = shares.map(Number);
      }
      const res = await authenticatedFetch('/predictions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFormError(body.error || 'Could not add that. Try again.');
        setHub(snapshot);
        return;
      }
      showToast({
        id: body.id,
        title: activeType === 'sweep'
          ? `Sweep opened · ${money(pot)}`
          : `Prediction added · ${money(stake)}`,
        sub: summary,
      });
      // Clear the slots ready for the next one — except a drawn pairs match,
      // where the sides are the day's own and there is nothing to re-pick.
      if (!prefilledFromDraw) setSides([[], []]);
      setJustLogged(body.id ?? null);
      setLogged({
        id: body.id ?? null,
        heading: activeType === 'sweep' ? 'Sweep logged' : 'Prediction logged',
        what: activeType === 'sweep'
          ? `Sweep · ${entrants.length} players · ${scoring} · ${
            places > 1 ? `top ${places} paid` : 'winner takes all'}`
          : summary,
        money: activeType === 'sweep'
          ? `${money(pot)} pot · ${money(stake)} each`
          : `${money(stake)} each`,
      });
      scrollToLogged.current = true;
      await load({ quiet: true, pick: round });
    } catch (err) {
      setFormError('Could not add that. Try again.');
      setHub(snapshot);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Take a bet off again.
   *
   * AN ORDINARY DELETE, so an ordinary confirmation. Nothing is lost that
   * cannot be re-entered in ten seconds: only an OPEN entry can go, it has no
   * debts on the ledger and nobody has been paid — the server refuses once it
   * has resolved. Type-to-confirm here would be the over-use this app's own
   * guidance warns about, which trains people to confirm without reading.
   */
  const remove = async () => {
    const target = removing;
    if (!target) return;
    setRemoving(null);
    const snapshot = hub;
    try {
      const res = await authenticatedFetch(`/predictions/${target.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setFormError(body.error || 'Could not remove that bet.');
        setHub(snapshot);
        return;
      }
      showToast({ title: 'Bet removed', sub: entryTitle(target, myPlayerId) });
      if (target.id === justLogged) setJustLogged(null);
      if (target.id === logged?.id) setLogged(null);
    } catch (err) {
      setFormError('Could not remove that bet.');
      setHub(snapshot);
    } finally {
      await load({ quiet: true, pick: round });
    }
  };

  const undo = async () => {
    const target = toast?.id;
    if (!target) return;
    setToast(null);
    if (target === justLogged) setJustLogged(null);
    // Undone, so there is nothing logged to report: back to the builder.
    if (target === logged?.id) setLogged(null);
    try {
      await authenticatedFetch(`/predictions/${target}`, { method: 'DELETE' });
    } finally {
      await load({ quiet: true, pick: round });
    }
  };

  const entries = (hub?.entries || []).filter((e) => e.status !== 'legacy');

  // Bring the confirmation on screen, once: the member pressed a button in the
  // sticky footer, and the builder they were looking at has just gone.
  useEffect(() => {
    if (!scrollToLogged.current || !loggedCard.current) return;
    scrollToLogged.current = false;
    loggedCard.current.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [logged, entries]);

  // Confirm, unless these exact terms are already open on this round — then
  // ask, because the likeliest reason is a second tap on the same bet.
  const requestConfirm = () => {
    const match = findDuplicate(entries, {
      kind: activeType,
      format: scoring,
      stake,
      roundNumber: round?.round_number ?? null,
      sides,
      placesPaid: places,
    });
    if (match) setDuplicate(match);
    else confirm();
  };

  if (loading && !hub) {
    return (
      <div className="pr-steps">
        <div className="pr-skeleton" style={{ height: 86 }} />
        <div className="pr-skeleton" style={{ height: 96 }} />
        <div className="pr-skeleton" style={{ height: 150 }} />
      </div>
    );
  }

  if (!round) {
    return (
      <div className="pr-steps">
        {loadError ? <div className="pr-error">{loadError}</div> : null}
        <Card>
          <div className="pr-empty">
            <strong>Nothing open to predict on.</strong>
            <br />
            Predictions open when a competition is added, and close as soon as
            the first score goes in.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="pr-steps">
        {loadError ? <div className="pr-error">{loadError}</div> : null}

        <Step number={1} title="Round">
          <button type="button" className="pr-round-card"
                  onClick={() => setRoundSheet(true)}>
            <span className="pr-round-card-main">
              <span className="pr-round-card-title">
                {round.scope === 'overall'
                  ? round.tournament_name
                  : roundTitle(round)}
              </span>
              <span className="pr-round-card-sub">
                {round.scope === 'overall'
                  ? `All ${round.round_count} rounds · ${
                      round.field_size} playing`
                  : roundDescription(round)}
              </span>
            </span>
            <span className="pr-round-card-change">
              Change <span aria-hidden="true">›</span>
            </span>
          </button>
        </Step>

        {/* A LOCKED ROUND IS READ-ONLY. It is here so the predictions
            riding on it stay visible while it is played — which is exactly
            when people want to watch them — but the golf has begun, so the
            terms are fixed. The server refuses a new entry or a change on it
            either way; this is the screen agreeing with the server rather
            than the guard itself. */}
        {locked ? (
          <div className="pr-locked-note">
            Every card in this round is in, so its predictions are locked.
            They settle as soon as the scores are checked.
          </div>
        ) : null}

        {!locked && logged ? (
          <section className="pr-step" ref={loggedCard}>
            <Card className="pr-logged-card">
              <div className="pr-logged-card-head" role="status">
                <span className="pr-logged-card-tick" aria-hidden="true">✓</span>
                <span className="pr-logged-card-heading">{logged.heading}</span>
              </div>
              <div className="pr-logged-card-what">{logged.what}</div>
              <div className="pr-logged-card-money">{logged.money}</div>
              <div className="pr-logged-card-note">
                It&rsquo;s in the list below.
              </div>
            </Card>
            <button type="button" className="pr-outline-btn pr-logged-card-again"
                    onClick={() => setLogged(null)}>
              Add another bet
            </button>
          </section>
        ) : null}

        {!locked && !logged ? (
          <>
        <Step number={2} title="Bet type">
          <div className="pr-types">
            {BET_TYPES.map((option) => {
              const disabled = !allowed.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`pr-type${
                    option.value === activeType ? ' is-selected' : ''}`}
                  disabled={disabled}
                  onClick={() => {
                    setType(option.value);
                    setScoring(defaultScoring(round, option.value));
                    setFormError(null);
                  }}
                >
                  <span className="pr-type-label">{option.label}</span>
                  <span className="pr-type-blurb">
                    {disabled ? 'Not on a match day' : option.blurb}
                  </span>
                </button>
              );
            })}
          </div>
        </Step>

        <Step number={3}
              title={activeType === 'sweep' ? 'Who is in'
                : activeType === 'pairs' ? 'The teams' : 'Who v who'}>
          {activeType === 'sweep' ? (
            <>
              <Card>
                <div className="pr-sweep-headline">
                  {/* Says what the sweep is ACTUALLY ranked on. It read "Best
                      nett score takes the pot" on a Stableford day, where the
                      sweep is saved and settled on points — and "takes the
                      pot" was wrong whenever Top 2 or Top 3 are paid. */}
                  Everyone in the round is entered — all{' '}
                  <strong>{players.length}</strong>. Ranked on{' '}
                  <strong>{scoring === 'stableford' ? 'Stableford points'
                    : scoring === 'gross' ? 'gross score' : 'nett score'}</strong>
                  {places > 1 ? `; the top ${places} are paid.` : '; the winner takes the pot.'}
                </div>
                <div className="pr-chip-list">
                  {players.map((p) => (
                    <span key={p.player_id} className="pr-player-chip">
                      <Avatar name={p.name} size={20} />
                      {shortName(p.name)}
                    </span>
                  ))}
                  {players.length === 0 ? (
                    <span className="pr-empty">
                      Nobody is entered in this round yet.
                    </span>
                  ) : null}
                </div>
              </Card>

              <Card>
                <div className="pr-sweep-label">How the pot is split</div>
                {SPLIT_OPTIONS.map((option) => (
                  <button
                    key={option.places}
                    type="button"
                    className={`pr-split-option${
                      option.places === places ? ' is-selected' : ''}`}
                    onClick={() => setPlaces(option.places)}
                  >
                    <span className={`pr-radio${
                      option.places === places ? ' is-selected' : ''}`}
                          aria-hidden="true" />
                    <span className="pr-split-option-main">
                      <span className="pr-split-option-label">
                        {option.label}
                      </span>
                      <span className="pr-split-option-sub">
                        {DEFAULT_SPLITS[option.places].join(' / ')}%
                      </span>
                    </span>
                    <span className="pr-split-option-money">
                      {splitMoney(option.places, pot)
                        .map((row) => money(row.amount)).join(' · ')}
                    </span>
                  </button>
                ))}

                {/* SET YOUR OWN. The presets are the figures most societies
                    use, but they are not the only ones — a society paying
                    45/35/20 had no way to say so. */}
                <button
                  type="button"
                  className={`pr-split-option${shares ? ' is-selected' : ''}`}
                  onClick={() => setShares(
                    shares ? null
                      : [...(DEFAULT_SPLITS[places] || DEFAULT_SPLITS[1])])}
                >
                  <span className={`pr-radio${shares ? ' is-selected' : ''}`}
                        aria-hidden="true" />
                  <span className="pr-split-option-main">
                    <span className="pr-split-option-label">Set your own</span>
                    <span className="pr-split-option-sub">
                      {shares ? 'Shares below' : 'Pick the shares yourself'}
                    </span>
                  </span>
                </button>

                {shares ? (
                  <div className="pr-shares">
                    <div className="pr-shares-head">
                      <span className="pr-shares-label">Places paid</span>
                      <span className="pr-stepper">
                        <button type="button" className="pr-stepper-btn"
                                disabled={places <= 1}
                                onClick={() => {
                                  const next = places - 1;
                                  setPlaces(next);
                                  setShares([...(DEFAULT_SPLITS[next]
                                    || DEFAULT_SPLITS[1])]);
                                }}>&minus;</button>
                        <span className="pr-stepper-value">{places}</span>
                        <button type="button" className="pr-stepper-btn"
                                disabled={places >= MAX_PLACES
                                  || places >= entrants.length}
                                onClick={() => {
                                  const next = places + 1;
                                  setPlaces(next);
                                  setShares([...(DEFAULT_SPLITS[next]
                                    || DEFAULT_SPLITS[1])]);
                                }}>+</button>
                      </span>
                    </div>

                    {shares.map((share, index) => (
                      <label key={index} className="pr-share-row">
                        <span className="pr-share-place">
                          {ordinal(index + 1)}
                        </span>
                        <input
                          className="pr-share-input"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          max="100"
                          value={share}
                          onChange={(event) => {
                            const next = [...shares];
                            const raw = event.target.value;
                            next[index] = raw === '' ? '' : Number(raw);
                            setShares(next);
                          }}
                        />
                        <span className="pr-share-pct">%</span>
                        <span className="pr-share-money">
                          {money(Math.round(pot * (Number(share) || 0)) / 100)}
                        </span>
                      </label>
                    ))}

                    {/* The running total, because a set of shares that does
                        not hand out the whole pot is not a set of terms. */}
                    <div className={`pr-shares-total${
                      sharesOk ? ' is-ok' : ''}`}>
                      {sharesOk
                        ? `Adds up to 100% · pot ${money(pot)}`
                        : `Adds up to ${Math.round(shares.reduce(
                          (sum, n) => sum + (Number(n) || 0), 0) * 100)
                          / 100}% — needs 100%`}
                    </div>
                  </div>
                ) : null}
              </Card>
            </>
          ) : (
            <>
              {prefilledFromDraw ? (
                <div className="pr-prefill-note">
                  Teams taken from this round&rsquo;s own pairings — just set
                  the stake.
                </div>
              ) : null}
              {activeType === 'pairs' ? (
                /* TWO CARDS, NOT ONE. A pair is a side — giving each its own
                   card and its own n/2 counter says how far off being filled
                   it is, which a single row of four slots cannot. */
                <>
                  {[0, 1].map((sideIndex) => (
                    <Card key={sideIndex}>
                      <div className="pr-team-head">
                        <span className="pr-sweep-label">
                          Team {sideIndex + 1}
                        </span>
                        <span className="pr-team-count">
                          {sides[sideIndex].length}/2
                        </span>
                      </div>
                      <div className="pr-team-slots">
                        {Array.from({ length: 2 }).map((_, slotIndex) => {
                          const id = sides[sideIndex][slotIndex];
                          return (
                            <Slot
                              key={slotIndex}
                              player={id != null ? byId[id] : null}
                              placeholder="Add player"
                              size={32}
                              compact
                              onClick={() => setPicking({
                                side: sideIndex, slot: slotIndex,
                              })}
                            />
                          );
                        })}
                      </div>
                    </Card>
                  ))}
                  <div className="pr-hint">
                    Tap any slot to pick or swap a player from this round.
                  </div>
                </>
              ) : (
                <>
                  <Card className="pr-vs-card">
                    <div className="pr-vs-side">
                      <Slot
                        player={sides[0][0] != null ? byId[sides[0][0]] : null}
                        placeholder="Add player"
                        onClick={() => setPicking({ side: 0, slot: 0 })}
                      />
                    </div>
                    <span className="pr-vs-mark">V</span>
                    <div className="pr-vs-side">
                      <Slot
                        player={sides[1][0] != null ? byId[sides[1][0]] : null}
                        placeholder="Add opponent"
                        onClick={() => setPicking({ side: 1, slot: 0 })}
                      />
                    </div>
                  </Card>
                  <div className="pr-hint">
                    Tap a slot to choose from the {players.length}
                    {' '}{players.length === 1 ? 'player' : 'players'} in this
                    round.
                  </div>
                </>
              )}
            </>
          )}
        </Step>

        <Step number={4}
              title={activeType === 'sweep' ? 'Entry per player' : 'Bet size'}>
          <Card>
            <div className="pr-amount">
              <button type="button" className="pr-amount-btn"
                      onClick={() => setStake((v) => Math.max(1, v - 5))}
                      aria-label="Less">−</button>
              <span className="pr-amount-value">{money(stake)}</span>
              <button type="button" className="pr-amount-btn"
                      onClick={() => setStake((v) => v + 5)}
                      aria-label="More">+</button>
            </div>

            <div className="pr-amount-caption">
              {activeType === 'sweep'
                ? `Pot ${money(pot)} · ${entrants.length} × ${money(stake)}`
                : activeType === 'pairs'
                  ? `Each man stakes ${money(stake)}`
                  : `Each side stakes ${money(stake)}`}
            </div>

            <div className="pr-presets">
              {PRESETS.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  className={`pr-preset${
                    amount === stake ? ' is-selected' : ''}`}
                  onClick={() => setStake(amount)}
                >
                  £{amount}
                </button>
              ))}
            </div>

            {!round.is_matchplay ? (
              <>
                <div className="pr-scoring-divider" />
                <div className="pr-sweep-label">Scoring</div>
                <div className="pr-scoring">
                  {[
                    // A sweep on a Stableford day is ranked on points by
                    // default (`defaultScoring`), and was offered only Nett and
                    // Gross — neither shown selected, and no way back once one
                    // was tapped.
                    ...(activeType === 'sweep' && round.format === 'Stableford'
                      ? [['stableford', 'Points', 'Stableford']] : []),
                    ['nett', 'Nett', 'Handicaps applied'],
                    ['gross', 'Gross', 'Raw scores'],
                  ].map(([option, label, blurb]) => (
                    <button
                      key={option}
                      type="button"
                      className={`pr-scoring-option${
                        option === scoring ? ' is-selected' : ''}`}
                      onClick={() => setScoring(option)}
                    >
                      <span className="pr-scoring-label">{label}</span>
                      <span className="pr-scoring-blurb">{blurb}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : null}

            {round.is_matchplay && activeType !== 'sweep' ? (
              <div className="pr-amount-caption">Scored on the match</div>
            ) : null}
          </Card>
        </Step>
          </>
        ) : null}

        <Step title={round.scope === 'overall'
                ? 'Logged on this competition' : 'Logged on this round'}
              hint={`${entries.length} ${
                entries.length === 1 ? 'bet' : 'bets'}`}>
          <Card list>
            {entries.length === 0 ? (
              <div className="pr-empty">
                Nothing logged yet. Bets you confirm are listed here.
              </div>
            ) : entries.map((entry) => {
              const isNew = entry.id === justLogged;
              const { amount, each, label } = entryAmount(entry);
              const view = loggedView(entry, myPlayerId, justLogged);
              return (
              <div key={entry.id}
                   className={`pr-logged${isNew ? ' is-new' : ''}`}>
                <div className="pr-logged-top">
                  <span className="pr-logged-kind">{view.heading}</span>
                  <span className="pr-logged-money">
                    {money(amount)}
                    <span className="pr-logged-money-unit">
                      {label === 'pot' ? ' pot' : ' each'}
                    </span>
                  </span>
                  {/* Shown only where the SERVER says this caller may remove
                      it, so the button and the endpoint cannot disagree. */}
                  {entry.can_amend ? (
                    <button
                      type="button"
                      className="pr-row-remove"
                      onClick={() => setRemoving(entry)}
                      aria-label={`Remove ${entryTitle(entry, myPlayerId)}`}
                    >
                      ✕
                    </button>
                  ) : (
                    // Holds the ✕'s width, so every row's money lines up.
                    <span className="pr-row-remove pr-row-remove--space"
                          aria-hidden="true" />
                  )}
                </div>

                {view.sides ? (
                  /* A pair goes on a line of its own: "A & B v C & D" wraps
                     at a phone's width wherever it likes, which left the "v"
                     stranded at the end of a line. The "v" travels with the
                     side after it either way. */
                  <div className={`pr-logged-sides${
                    view.sides.some((side) => side.length > 1)
                      ? ' pr-logged-sides--stacked' : ''}`}>
                    {view.sides.map((side, index) => (
                      <span key={index} className="pr-logged-side">
                        {index > 0 ? (
                          <span className="pr-logged-v">v</span>
                        ) : null}
                        {side.join(' & ')}
                      </span>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="pr-logged-count">
                      {view.entrants.length}{' '}
                      {view.entrants.length === 1 ? 'player' : 'players'}
                      {' · '}{money(each)} each
                    </div>
                    <div className="pr-logged-entrants">
                      {view.entrants.join(', ')}
                    </div>
                  </>
                )}

                {view.status ? (
                  <div className="pr-logged-status">{view.status}</div>
                ) : null}
              </div>
              );
            })}
          </Card>
        </Step>
      </div>

      {/* What you are building, what it costs, one button — so it belongs
          with the builder and comes off with it on a locked round. */}
      {!locked && !logged ? (
      <div className="pr-footer">
        {formError ? <div className="pr-error">{formError}</div> : null}
        <div className="pr-footer-row">
          <span className="pr-footer-summary">
            {summary || 'No bet set up yet'}
          </span>
          <span className="pr-footer-total">{money(total)}</span>
        </div>
        <button type="button" className="pr-cta"
                disabled={!valid || submitting} onClick={requestConfirm}>
          {submitting ? 'Adding…'
            : !valid
              ? (activeType === 'sweep'
                ? 'Nobody to enter yet' : 'Select players to continue')
              : activeType === 'sweep' ? 'Confirm sweep' : 'Confirm prediction'}
        </button>
      </div>
      ) : null}

      {roundSheet ? (
        <RoundSheet
          rounds={rounds}
          selected={round}
          onPick={(picked) => { setRoundSheet(false); load({ pick: picked }); }}
          onClose={() => setRoundSheet(false)}
        />
      ) : null}

      {picking ? (
        <PlayerPickerModal
          players={players}
          excluded={sides[picking.side === 0 ? 1 : 0]}
          current={sides[picking.side][picking.slot] ?? null}
          title={activeType === 'pairs'
            ? `Team ${picking.side + 1}`
            : picking.side === 0 ? 'Pick a player' : 'Pick an opponent'}
          onPick={(id) => assign(picking.side, picking.slot, id)}
          onClear={() => clearSlot(picking.side, picking.slot)}
          onClose={() => setPicking(null)}
        />
      ) : null}

      <ConfirmationModal
        isOpen={Boolean(duplicate)}
        title="Already logged?"
        message={duplicate
          ? `${entryTitle(duplicate, myPlayerId)} · ${
            money(entryAmount(duplicate).amount)} is already on this round `
            + 'on the same terms. Log it a second time?'
          : ''}
        confirmText="Log another"
        cancelText="No, leave it"
        onConfirm={() => { setDuplicate(null); confirm(); }}
        onCancel={() => setDuplicate(null)}
      />

      <ConfirmationModal
        tone="danger"
        isOpen={Boolean(removing)}
        title="Remove this bet?"
        message={removing
          ? `${entryTitle(removing, myPlayerId)} · ${money(removing.stake)}. `
            + 'Nothing has been scored on it yet, so you can add it again.'
          : ''}
        confirmText="Remove"
        cancelText="Keep it"
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />

      {toast ? (
        <div className="pr-toast" role="status">
          <div className="pr-toast-main">
            <div className="pr-toast-title">{toast.title}</div>
            <div className="pr-toast-sub">{toast.sub}</div>
          </div>
          {toast.id ? (
            <button type="button" className="pr-toast-undo" onClick={undo}>
              Undo
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export default PredictionsHub;
